import { TEXT_MAX_LENGTH } from '../models/messaging-message';
import { action } from '@ember/object';
import { filterSystemAndCurrentUser } from '../utils/messaging/user';
import { fn } from '@ember/helper';
import { getMessageDateLabel } from '../utils/messaging/format-date';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { waitForNext } from 'ember-simplepractice/utils/waiters';
import Component from '@glimmer/component';
import ConversationMessage from './conversation-message.gjs';
import MessagingNotification from './messaging-notification.gjs';
import MessagingPage from './messaging-page.gjs';
import SharedInfinityLoader from 'ember-simplepractice/components/shared/infinity-loader';
import styles from './conversation-page.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import textareaAutosize from 'ember-simplepractice/modifiers/textarea-autosize';

export default class ConversationPage extends Component {
  @service messaging;
  @service messagingConversation;
  @service('websocket/messagingWebsocket') messagingWebsocket;

  @tracked newMessage = '';

  chatElement;
  messageMaxLength = TEXT_MAX_LENGTH;

  onInsert = modifier(async e => {
    this.chatElement = e;
  });

  get messagesByDate() {
    const groupedMessagesMap = new Map();
    this.messagingConversation.messages?.forEach(message => {
      let dateKey = new Date(message.createdAt).toDateString();

      if (!groupedMessagesMap.has(dateKey)) {
        groupedMessagesMap.set(dateKey, []);
      }

      groupedMessagesMap.get(dateKey).unshift(message);
    });
    return groupedMessagesMap;
  }

  get reversedDates() {
    return Array.from(this.messagesByDate.entries())
      .map(([date]) => date)
      .reverse();
  }

  get filteredUsers() {
    return filterSystemAndCurrentUser(
      this.messagingConversation.selectedConversation?.users,
      this.messaging.currentProfile
    );
  }

  get userNames() {
    return this.filteredUsers.map(user => user.name).join(' & ');
  }

  get isLoading() {
    return (
      this.messagingConversation.getMessages.isRunning ||
      this.messagingConversation.fetchInactiveReasons.isRunning
    );
  }

  get showInactiveReasons() {
    return this.messagingConversation.inactiveReasons.length && this.messagingWebsocket.isConnected;
  }

  get isSendDisabled() {
    const noTextEntered = !this.newMessage.trim().length;
    const isNotConnected = !this.messagingWebsocket.isConnected;
    const hasInactiveReasons = this.messagingConversation.inactiveReasons.length > 0;
    return noTextEntered || this.hasInvalidCharacterCount || isNotConnected || hasInactiveReasons;
  }

  get hasInvalidCharacterCount() {
    return this.newMessage.length > this.messageMaxLength;
  }

  handleTextareaInput = ({ target: textarea }) => {
    this.newMessage = textarea.value;
  };

  @action
  getMessagesForDate(date) {
    return this.messagesByDate.get(date);
  }

  @action
  async loadMoreMessages(model) {
    if (!model.canLoadMore) return;

    let previousScrollTop = this.chatElement.scrollTop;
    await this.messagingConversation.loadMoreMessages.perform(model);
    await waitForNext(); // Waiting for the DOM to update before restoring scroll position
    this.chatElement.scrollTop = previousScrollTop;
  }

  @action
  close() {
    this.messagingConversation.closeConversationPage();
  }

  @action
  retryConnection() {
    this.messagingWebsocket.connectSocket();
  }

  <template>
    <MessagingPage class="{{styles.component}} conversation-page">
      <:header>
        <button
          class="button transparent back-button"
          type="button"
          data-test-conversation-page-back-button
          {{on "click" this.close}}
        >
          {{svgJar "messaging-chevron-left"}}
        </button>
        <span>{{this.userNames}}</span>
      </:header>
      <:default>
        <div class="content" data-test-conversation-page-content {{this.onInsert}}>
          {{#if this.isLoading}}
            <div class="spinner" data-test-loading-spinner>
              {{svgJar "messaging-spinner"}}
            </div>
          {{else}}
            <div class="messages">
              {{#if this.messagingConversation.messages}}
                <SharedInfinityLoader
                  @model={{this.messagingConversation.messages}}
                  @infinityLoad={{this.loadMoreMessages}}
                  @triggerOffset={{0}}
                />
              {{/if}}
              {{#each this.reversedDates as |date|}}
                <div class="date-label">{{getMessageDateLabel date}}</div>
                {{#each (this.getMessagesForDate date) as |message|}}
                  <ConversationMessage @message={{message}} />
                {{/each}}
              {{/each}}
            </div>
          {{/if}}
        </div>
      </:default>
      <:footer>
        {{#unless this.messagingWebsocket.isConnected}}
          <MessagingNotification
            @type="error"
            @message={{t "device_offline_error"}}
            @buttonLabel={{t "retry_connection"}}
            @onButtonClick={{this.retryConnection}}
          />
        {{/unless}}
        {{#if this.showInactiveReasons}}
          {{#each this.messagingConversation.inactiveReasons as |reason|}}
            <MessagingNotification
              @type="info"
              @message={{reason.message}}
              @buttonLabel={{reason.action}}
              @onButtonClick={{fn this.messaging.emitEvent reason.handler reason.meta}}
            />
          {{/each}}
        {{/if}}
        <div class="input-area">
          <textarea
            class="text-input"
            placeholder={{t "message_placeholder"}}
            aria-label="Message"
            {{on "input" this.handleTextareaInput}}
            {{textareaAutosize}}
            data-test-message-input
          />

          <button
            disabled={{this.isSendDisabled}}
            class="button primary send-button"
            type="button"
            data-test-message-send
          >
            {{t "send"}}
          </button>
        </div>
        <div class="input-sub-text">
          <span data-test-hint>
            <strong>{{t "new_line_hint.keys"}}</strong>
            {{t "new_line_hint.effect"}}
          </span>
          <span class={{if this.hasInvalidCharacterCount "invalid"}} data-test-character-count>
            {{or this.newMessage.length 0}}/{{this.messageMaxLength}}
          </span>
        </div>
      </:footer>
    </MessagingPage>
  </template>
}
